import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SafeIcon from '../common/SafeIcon';

const featureCategories = [
  {
    id: 'reputation',
    title: 'Reputation',
    intro: 'Collect, organize, reply to, and learn from the customer feedback that shapes trust.',
    features: [
      { 
        name: 'Review Aggregator', 
        desc: 'Pull reviews into one place so the business can see what customers are saying without checking every platform manually.', 
        badge: null,
        bottomLine: 'When reviews live in one place, patterns get easier to see. That means faster replies, fewer missed problems, and better decisions from real customer words.' 
      },
      { 
        name: 'Review Feed', 
        desc: 'A clean stream of recent customer feedback, ratings, replies, and reputation activity.', 
        badge: null,
        bottomLine: 'A messy reputation is hard to fix. A clear feed shows you exactly what customers are feeling right now, so you never miss a chance to build trust.' 
      },
      { 
        name: 'Review Link', 
        desc: 'One simple link customers can use to leave feedback or a public review.', 
        badge: null,
        bottomLine: 'Friction kills reviews. Giving customers one simple, direct path means more of them will actually leave the 5-star rating they promised.' 
      },
      { 
        name: 'Experience Filter', 
        desc: 'Guide unhappy customers into private feedback and happy customers toward public reviews.', 
        badge: null,
        bottomLine: 'Unhappy customers just want to be heard. Catching their frustration privately saves your public rating and gives you a chance to fix the relationship.' 
      },
      { 
        name: 'Private Feedback', 
        desc: 'Capture low-star experiences before they become public reputation damage.', 
        badge: null,
        bottomLine: 'Fixing a problem before it hits Google protects your score. It turns a potential 1-star disaster into a saved customer.' 
      },
      { 
        name: 'Video Testimonials', 
        desc: 'Collect richer customer praise through short video testimonials.', 
        badge: null,
        bottomLine: 'Video makes trust easier to feel. A real customer on camera can say what a polished ad never could, converting viewers into buyers.' 
      },
      { 
        name: 'Custom Review Sites', 
        desc: 'Send customers to the platforms that matter most for the business.', 
        badge: null,
        bottomLine: 'Not every business needs Google reviews. Sending customers to the industry sites that actually drive your revenue builds authority where it counts.' 
      },
      { 
        name: 'Thank You Page', 
        desc: 'Send happy customers to a branded follow-up page after they leave a review.', 
        badge: null,
        bottomLine: 'A cold transaction forgets the customer. A branded thank-you makes them feel valued, turning a one-time buyer into a returning regular.' 
      },
      { 
        name: 'Review Replies', 
        desc: 'Reply quickly and consistently to reviews with Ruby Replies.', 
        badge: null,
        bottomLine: 'A good reply shows future customers you are paying attention. Ruby helps every response sound human, consistent, and in your brand voice.' 
      },
      { 
        name: 'Sentiment Analysis', 
        desc: 'Understand what customers love, where friction appears, and what patterns keep repeating.', 
        badge: null,
        bottomLine: 'Guessing what customers want is expensive. Knowing exactly what they love and hate lets you fix operations and double down on what sells.' 
      },
      { 
        name: 'Notifications', 
        desc: 'Get alerted when new reviews or private feedback come in.', 
        badge: null,
        bottomLine: 'Speed matters when trust is on the line. Getting alerted instantly means you can thank a fan or defuse a crisis before competitors even wake up.' 
      },
    ]
  },
  {
    id: 'visibility',
    title: 'Visibility',
    intro: 'Turn customer language into stronger local search, better profiles, and clearer authority signals.',
    features: [
      { 
        name: 'Ranking Analysis', 
        desc: 'See how the business is showing up locally and where reputation signals may be helping or hurting.', 
        badge: null,
        bottomLine: 'If people cannot find you, they cannot choose you. This shows where reputation is helping or hurting local visibility.' 
      },
      { 
        name: 'Competitor Analysis', 
        desc: 'Compare review volume, star rating, response activity, and reputation strength against competitors.', 
        badge: null,
        bottomLine: 'You lose money when competitors look more trusted. Knowing their weaknesses lets you capture the customers they are dropping.' 
      },
      { 
        name: 'LLMs Analysis', 
        desc: 'Track how the business appears in AI-generated answers from tools like ChatGPT, Gemini, and Perplexity.', 
        badge: 'SOON',
        bottomLine: 'People are asking AI where to eat, shop, and hire. If ChatGPT does not know why you are the best, you are invisible to the next generation of search.' 
      },
      { 
        name: 'Performance', 
        desc: 'Monitor reputation activity, review growth, visibility, and customer feedback trends.', 
        badge: null,
        bottomLine: 'What gets measured gets managed. Watching your trust signals grow proves your reputation work is actually turning into local authority.' 
      },
      { 
        name: 'Keywords Analysis', 
        desc: 'Find the words customers naturally use when describing the business.', 
        badge: null,
        bottomLine: 'Using your customers\' exact words in your marketing makes them feel understood. It is the easiest way to write copy that actually converts.' 
      },
      { 
        name: 'Listing Management', 
        desc: 'Keep important business information consistent and accurate.', 
        badge: null,
        bottomLine: 'Wrong hours or bad directions cost you foot traffic. Keeping your details locked and accurate ensures customers end up at your door, not a competitor\'s.' 
      },
      { 
        name: 'Website Widgets', 
        desc: 'Display fresh reviews and testimonials directly on the business website.', 
        badge: null,
        bottomLine: 'Your website needs to close the deal. Showing fresh, real customer praise right where people buy turns hesitation into confidence.' 
      },
      { 
        name: 'Google Photos', 
        desc: 'Use customer and business visuals to strengthen local trust signals.', 
        badge: null,
        bottomLine: 'A dead profile looks like a closed business. Fresh photos prove you are active, busy, and trusted by the community.' 
      },
      { 
        name: 'Rich Snippets & Schema', 
        desc: 'Help search engines better understand reviews, ratings, and business information.', 
        badge: 'NEW',
        bottomLine: 'Google rewards businesses it understands. Feeding review data directly into search code makes your stars show up in results, driving more clicks.' 
      },
      { 
        name: 'Automated Website', 
        desc: 'Generate local authority pages from real business data, services, reviews, and customer language.', 
        badge: 'NEW',
        bottomLine: 'Building a site from scratch takes weeks. Letting your reviews and services build it automatically gets you a trusted local presence in minutes.' 
      },
      { 
        name: 'Google Posts', 
        desc: 'Publish timely updates and review-powered content to the Google Business Profile.', 
        badge: null,
        bottomLine: 'Fresh updates tell Google and customers the business is alive. Turn reviews and customer praise into visible proof directly on the search page.' 
      },
      { 
        name: 'Google Hours', 
        desc: 'Keep business hours accurate so customers know when to visit.', 
        badge: null,
        bottomLine: 'Nothing ruins trust faster than a closed door during open hours. Keeping this accurate respects your customers\' time.' 
      },
      { 
        name: 'Multi Locations', 
        desc: 'Manage reputation signals across multiple locations.', 
        badge: null,
        bottomLine: 'Inconsistent reputation across branches hurts the whole brand. Managing everything together ensures every location pulls its own weight.' 
      },
    ]
  },
  {
    id: 'engagement',
    title: 'Engagement',
    intro: 'Turn reviews into content people can see, share, and trust.',
    features: [
      { 
        name: 'Review Posts', 
        desc: 'Turn strong reviews into ready-to-share social posts.', 
        badge: null,
        bottomLine: 'Great reviews shouldn\'t die on Google. Turning them into visual posts squeezes every drop of marketing value out of a happy customer.' 
      },
      { 
        name: 'Review Stories / Reels', 
        desc: 'Create short-form content from praise, testimonials, and customer moments.', 
        badge: null,
        bottomLine: 'People buy from businesses that feel active and celebrated. Short-form video proof catches attention where your customers already scroll.' 
      },
      { 
        name: 'Blog to Social', 
        desc: 'Repurpose website content into smaller social posts.', 
        badge: 'NEW',
        bottomLine: 'Creating content from scratch is exhausting. Breaking your best authority pages into social posts keeps you visible without the extra work.' 
      },
      { 
        name: 'Social Agent', 
        desc: 'Use Annie Amplifies to turn praise into social proof.', 
        badge: null,
        bottomLine: 'Annie turns praise into posts, so your best customer words do not die inside a review feed. She does the heavy lifting of keeping your social proof alive.' 
      },
      { 
        name: 'Custom Prompt', 
        desc: 'Adjust the tone and style of AI-generated content to match the business.', 
        badge: null,
        bottomLine: 'Generic content sounds cheap. Tuning the AI ensures every post sounds like you, protecting your brand\'s unique local flavor.' 
      },
    ]
  },
  {
    id: 'ai-agents',
    title: 'AI Agents',
    intro: 'Meet the AI helpers that move customer words through the system.',
    features: [
      { 
        name: 'RepUpgrade.ai', 
        desc: 'The reputation brain. It reads patterns, answers questions, and recommends next steps.', 
        badge: null,
        bottomLine: 'Staring at data doesn\'t grow a business. Having an AI that tells you exactly what to fix next turns overwhelming feedback into clear revenue steps.' 
      },
      { 
        name: 'Ruby Replies', 
        desc: 'Replies to reviews in the business’s tone, length, and language.', 
        badge: null,
        bottomLine: 'Ignoring reviews looks bad, but writing them takes time. Ruby handles the replies so you protect your reputation without staring at a keyboard.' 
      },
      { 
        name: 'Annie Amplifies', 
        desc: 'Turns praise into social posts, stories, and content prompts.', 
        badge: null,
        bottomLine: 'Marketing usually means starting from a blank page. Annie turns the nice things customers say into content that brings new customers in.' 
      },
      { 
        name: 'Mabel Writes', 
        desc: 'Turns customer feedback into helpful website content and local authority pages.', 
        badge: null,
        bottomLine: 'Mabel turns repeated customer language into blogs, FAQs, and website copy. That helps the business explain what it does using words customers already trust.' 
      },
      { 
        name: 'AI Review Suggestions', 
        desc: 'Helps customers write more useful reviews by suggesting clear, specific review language.', 
        badge: null,
        bottomLine: 'Customers want to help, but they don\'t know what to write. Suggesting specific words gets you detailed reviews that actually improve local search.' 
      },
      { 
        name: 'Custom Agent Prompt', 
        desc: 'Tune the AI helpers to match the business voice.', 
        badge: null,
        bottomLine: 'AI sounds robotic when it doesn\'t know your business. Tuning the helpers ensures they speak with your local expertise and charm.' 
      },
      { 
        name: 'AI Sentiment Analysis', 
        desc: 'Find emotional patterns hiding inside customer comments.', 
        badge: null,
        bottomLine: 'Reading every review is impossible at scale. Finding the hidden emotional patterns tells you exactly why people are buying or leaving.' 
      },
      { 
        name: 'AI Monthly Summary', 
        desc: 'Summarize review growth, customer sentiment, wins, risks, and next steps.', 
        badge: null,
        bottomLine: 'Owners don\'t need more dashboards; they need answers. A clean summary tells you if you are winning or losing trust this month.' 
      },
    ]
  },
  {
    id: 'automation',
    title: 'Automation',
    intro: 'Keep review requests, reminders, updates, and workflows moving without manual follow-up.',
    features: [
      { 
        name: 'Send Requests', 
        desc: 'Send review requests through text, email, WhatsApp, QR code, or manual link.', 
        badge: null,
        bottomLine: 'Hoping customers leave a review doesn\'t work. Asking them directly through the channel they prefer guarantees a steady stream of fresh proof.' 
      },
      { 
        name: 'Templates', 
        desc: 'Use pre-written request templates that match the business voice.', 
        badge: null,
        bottomLine: 'Staring at a blank screen wastes time. Pre-written, brand-matched templates mean you can send a perfect request in two clicks.' 
      },
      { 
        name: 'Contacts', 
        desc: 'Manage customer lists and contact details.', 
        badge: null,
        bottomLine: 'Your customer list is your most valuable asset. Keeping it organized means you always know who to ask for feedback and referrals.' 
      },
      { 
        name: 'Auto-Send', 
        desc: 'Automatically send review requests after a visit, service, or completed job.', 
        badge: null,
        bottomLine: 'You get busy and forget to ask. Automating the request ensures your review count grows while you focus on running the business.' 
      },
      { 
        name: 'Opt-In', 
        desc: 'Let customers opt in before receiving review request messages.', 
        badge: null,
        bottomLine: 'Spamming people hurts trust. Getting permission first keeps your communication clean and your brand respected.' 
      },
      { 
        name: 'CSV Upload', 
        desc: 'Upload customer lists in bulk.', 
        badge: null,
        bottomLine: 'Manual entry is a bottleneck. Bulk uploading lets you activate hundreds of past happy customers into fresh reviews instantly.' 
      },
      { 
        name: 'Reminders', 
        desc: 'Follow up with customers who have not opened or responded to a review request.', 
        badge: null,
        bottomLine: 'People forget, not because they don\'t care, but because they are busy. A gentle nudge often doubles the amount of reviews you collect.' 
      },
      { 
        name: 'Google Sheet Integration', 
        desc: 'Trigger review requests from new or updated Google Sheet rows.', 
        badge: 'AUTOMATION',
        bottomLine: 'Manual data entry drops the ball. Connecting to your existing sheets means a closed deal automatically triggers a review request.' 
      },
      { 
        name: 'WhatsApp Requests', 
        desc: 'Send review requests through WhatsApp where supported.', 
        badge: null,
        bottomLine: 'Emails get ignored. Sending a request where your customers actually chat means higher open rates and faster reviews.' 
      },
      { 
        name: 'Zapier', 
        desc: 'Connect RepUpgrade workflows to other tools.', 
        badge: 'AUTOMATION',
        bottomLine: 'Isolated tools create extra work. Connecting your reputation engine to your CRM means everything flows together without you touching it.' 
      },
      { 
        name: 'Webhooks', 
        desc: 'Trigger external workflows when reputation events happen.', 
        badge: 'API',
        bottomLine: 'Advanced businesses need custom triggers. Webhooks let your reputation data instantly talk to your internal systems for seamless operations.' 
      },
      { 
        name: 'API', 
        desc: 'Support advanced automation and future integrations.', 
        badge: 'API',
        bottomLine: 'Scaling requires custom connections. An open API means your reputation engine can grow and adapt precisely to how your business works.' 
      },
      { 
        name: 'Daily Review Notifications', 
        desc: 'Send a daily summary of new reviews when activity happens.', 
        badge: null,
        bottomLine: 'Surprises in reputation are usually bad. A daily digest keeps your finger on the pulse so you know exactly what customers experienced yesterday.' 
      },
      { 
        name: 'Monthly Report Notification', 
        desc: 'Send a monthly reputation summary with wins, risks, and trends.', 
        badge: null,
        bottomLine: 'You need to know if the strategy is working. A monthly summary proves the value of your reputation effort and highlights where to focus next.' 
      },
    ]
  },
  {
    id: 'team',
    title: 'Team & Access',
    intro: 'Give the right people access, track who creates trust, and turn reputation into a team sport.',
    features: [
      { 
        name: 'Employees Tracking', 
        desc: 'Track which team member generated or influenced reviews.', 
        badge: 'NEW',
        bottomLine: 'You cannot reward what you do not measure. Knowing who drives 5-star experiences lets you train the rest of the team to do the same.' 
      },
      { 
        name: 'Employees Leaderboard', 
        desc: 'Create friendly competition around customer experience.', 
        badge: 'NEW',
        bottomLine: 'When the team can see who creates great experiences, reputation becomes a game worth winning. It turns staff into trust-builders.' 
      },
      { 
        name: 'Location Managers', 
        desc: 'Give location-specific access to managers or owners.', 
        badge: null,
        bottomLine: 'Founders shouldn\'t micromanage every branch. Giving managers local control empowers them to fix their own reputation leaks.' 
      },
      { 
        name: 'User Management', 
        desc: 'Control who can view, manage, approve, and publish.', 
        badge: null,
        bottomLine: 'Too much access leads to mistakes. Controlling who can publish or reply protects the brand while still getting work done.' 
      },
      { 
        name: 'Multi Locations', 
        desc: 'Support businesses with more than one location.', 
        badge: null,
        bottomLine: 'Franchises live and die by consistency. Managing all locations from one view ensures no single branch is dragging down the brand.' 
      },
      { 
        name: 'QR Codes by Employee', 
        desc: 'Give team members individual QR codes to track review activity.', 
        badge: null,
        bottomLine: 'Customers tip the server, not the restaurant. Individual QR codes tie a great review directly to the person who earned it.' 
      },
      { 
        name: 'Email & SMS History', 
        desc: 'See what was sent, when, and through which channel.', 
        badge: null,
        bottomLine: 'Accountability prevents spam. Seeing exactly what was sent stops double-messaging and keeps your customer communication professional.' 
      },
      { 
        name: 'Custom Menu Links', 
        desc: 'Add helpful links into the app experience.', 
        badge: null,
        bottomLine: 'Jumping between tabs breaks focus. Adding your essential tools right into the menu keeps the team working in one central hub.' 
      },
      { 
        name: 'Knowledge Base', 
        desc: 'Give users simple guidance inside the platform.', 
        badge: null,
        bottomLine: 'Training new staff on software is expensive. Built-in guidance means the team figures it out without asking you for help.' 
      },
    ]
  },
  {
    id: 'app-experience',
    title: 'App Experience',
    intro: 'Make reputation management easy to use from the dashboard or mobile app.',
    features: [
      { 
        name: 'Client Mode AI Chat', 
        desc: 'Ask the app questions like "How is my reputation today?" or "What should I improve first?"', 
        badge: 'AI',
        bottomLine: 'Clicking through menus wastes time. Asking the app directly "what should I do today" turns a software tool into a strategic advisor.' 
      },
      { 
        name: 'Mobile App / PWA', 
        desc: 'Install the app on a phone and manage reputation from the field.', 
        badge: 'APP',
        bottomLine: 'The owner should not need to be at a desk to protect trust. Reputation work should move with the business, right from your pocket.' 
      },
      { 
        name: 'Review Filters', 
        desc: 'Filter by platform, rating, replied/unreplied, and review type.', 
        badge: null,
        bottomLine: 'Drowning in data hides the real issues. Filtering lets you instantly find the 1-star complaints or the unreplied praise that needs attention.' 
      },
      { 
        name: 'Kiosk Mode Editor', 
        desc: 'Customize the kiosk review experience for in-store or front-desk use.', 
        badge: null,
        bottomLine: 'A clunky interface stops reviews in their tracks. Customizing the digital checkout experience makes leaving a review feel completely natural.' 
      },
      { 
        name: 'Download Kiosk Mode', 
        desc: 'Download and print kiosk assets.', 
        badge: null,
        bottomLine: 'Physical businesses need physical tools. Printing your custom QR codes turns waiting rooms and front desks into review-generating machines.' 
      },
      { 
        name: 'Review Link Live Sync', 
        desc: 'Preview review link changes as they are edited.', 
        badge: null,
        bottomLine: 'Guessing what the customer sees causes mistakes. Watching the link update live guarantees your branding looks perfect before you hit send.' 
      },
      { 
        name: 'Custom Review Link URL', 
        desc: 'Customize the review link path for the business.', 
        badge: null,
        bottomLine: 'A messy URL looks like spam. A clean, branded link builds confidence before the customer even clicks.' 
      },
      { 
        name: 'New Design Mobile App', 
        desc: 'A cleaner mobile experience for reputation tasks.', 
        badge: 'APP',
        bottomLine: 'Complex apps don\'t get used. A clean, focused mobile design ensures your team actually enjoys managing the business reputation.' 
      },
      { 
        name: 'Character Counter', 
        desc: 'Show message length for SMS templates.', 
        badge: null,
        bottomLine: 'SMS gets expensive when messages run long. Knowing your text length keeps communication punchy and costs under control.' 
      },
      { 
        name: 'Plain Text Emails', 
        desc: 'Improve deliverability with simple plain-text emails.', 
        badge: null,
        bottomLine: 'Fancy emails land in the promotions tab. Simple text feels like a personal message from the owner, drastically improving open rates.' 
      },
    ]
  }
];

// Helper to style badges based on their text
const getBadgeStyles = (text) => {
  switch (text) {
    case 'NEW': return 'bg-[#2CB5B0]/10 text-[#2CB5B0] border-[#2CB5B0]/20';
    case 'SOON': return 'bg-[#F2C230]/10 text-[#D35E0E] border-[#F2C230]/20';
    case 'AI': return 'bg-[#409FA1]/10 text-[#409FA1] border-[#409FA1]/20';
    case 'AUTOMATION': return 'bg-[#244A49]/10 text-[#244A49] border-[#244A49]/20';
    case 'API': return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'APP': return 'bg-blue-100 text-blue-700 border-blue-200';
    default: return 'bg-gray-100 text-gray-600 border-gray-200';
  }
};

export default function PlaygroundFeatures() {
  const [activeCategoryId, setActiveCategoryId] = useState(featureCategories[0].id);
  const [expandedFeature, setExpandedFeature] = useState(null);

  const activeCategory = featureCategories.find(c => c.id === activeCategoryId);

  const handleToggleFeature = (featureName) => {
    setExpandedFeature(expandedFeature === featureName ? null : featureName);
  };

  const handleCategorySwitch = (id) => {
    setActiveCategoryId(id);
    setExpandedFeature(null); // Close any open feature when switching categories
  };

  return (
    <div className="min-h-screen bg-[#F7F8F7] font-sans selection:bg-[#409FA1] selection:text-white flex flex-col">
      <Navbar />

      <main className="flex-grow pt-32 pb-24">
        {/* Header Section */}
        <div className="max-w-7xl mx-auto px-6 mb-16 text-center md:text-left">
          <span className="inline-block py-1 px-3 rounded-full bg-white border border-gray-200 text-[#F49558] text-xs font-bold tracking-wider font-michroma mb-6 shadow-sm">
            THE REPUTATION ENGINE
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#101F1F] tracking-tight mb-6 max-w-4xl">
            Everything RepUpgrade can help you do.
          </h1>
          <p className="text-xl text-[#101F1F]/70 max-w-3xl leading-relaxed mb-10">
            From review collection to AI replies, social proof, insights, team tracking, and automation — explore the tools inside the RepUpgrade app.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <Link to="/playground" className="w-full sm:w-auto bg-[#F49558] hover:bg-[#D35E0E] text-white text-lg font-bold py-4 px-8 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2">
              Try the App Live <SafeIcon name="ArrowRight" className="w-5 h-5" />
            </Link>
            <Link to="/download" className="w-full sm:w-auto bg-white hover:bg-gray-50 text-[#101F1F] border border-gray-200 shadow-sm text-lg font-bold py-4 px-8 rounded-xl transition-all flex items-center justify-center">
              Download the App
            </Link>
          </div>
        </div>

        {/* Two-Column Layout */}
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row gap-12">
          
          {/* Left Column: Category Navigation */}
          <div className="w-full lg:w-1/4">
            {/* Mobile: Horizontal scroll pills */}
            <div className="flex lg:hidden overflow-x-auto pb-4 mb-2 -mx-6 px-6 space-x-2 scrollbar-hide">
              {featureCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategorySwitch(cat.id)}
                  className={`flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                    activeCategoryId === cat.id 
                      ? 'bg-[#244A49] text-white shadow-md' 
                      : 'bg-white text-[#101F1F]/70 border border-gray-200 hover:border-[#409FA1] hover:text-[#101F1F]'
                  }`}
                >
                  {cat.title}
                </button>
              ))}
            </div>

            {/* Desktop: Sticky Vertical Nav */}
            <div className="hidden lg:flex flex-col gap-2 sticky top-32">
              <h3 className="text-xs font-bold text-[#101F1F]/50 uppercase tracking-widest mb-4 px-4 font-michroma">
                Categories
              </h3>
              {featureCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategorySwitch(cat.id)}
                  className={`text-left px-4 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-between group ${
                    activeCategoryId === cat.id 
                      ? 'bg-white text-[#244A49] shadow-sm border border-gray-100' 
                      : 'text-[#101F1F]/60 hover:bg-white/50 hover:text-[#101F1F]'
                  }`}
                >
                  <span>{cat.title}</span>
                  <span className={`text-xs font-normal ${activeCategoryId === cat.id ? 'text-[#409FA1]' : 'text-transparent group-hover:text-gray-400'}`}>
                    {cat.features.length}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Right Column: Active Category Features */}
          <div className="w-full lg:w-3/4 min-h-[600px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCategory.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mb-10">
                  <h2 className="text-3xl font-bold text-[#101F1F] mb-4 font-michroma tracking-tight flex items-center gap-3">
                    {activeCategory.title}
                    <span className="text-sm font-sans font-normal text-[#101F1F]/50 bg-gray-200/50 px-3 py-1 rounded-full">
                      {activeCategory.features.length} tools
                    </span>
                  </h2>
                  <p className="text-lg text-[#101F1F]/70 max-w-2xl">{activeCategory.intro}</p>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  {activeCategory.features.map((feature, index) => {
                    const isOpen = expandedFeature === feature.name;
                    return (
                      <div 
                        key={feature.name} 
                        className={`border-b border-gray-100 last:border-b-0 transition-colors ${isOpen ? 'bg-[#F7F8F7]/50' : 'hover:bg-gray-50'}`}
                      >
                        <button
                          onClick={() => handleToggleFeature(feature.name)}
                          className="w-full px-6 py-5 flex items-center justify-between focus:outline-none"
                        >
                          <div className="flex items-center gap-4">
                            <span className={`text-base font-bold transition-colors ${isOpen ? 'text-[#244A49]' : 'text-[#101F1F]'}`}>
                              {feature.name}
                            </span>
                            {feature.badge && (
                              <span className={`text-[10px] font-bold font-michroma tracking-wider px-2 py-0.5 rounded border ${getBadgeStyles(feature.badge)}`}>
                                {feature.badge}
                              </span>
                            )}
                          </div>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${isOpen ? 'bg-[#244A49] text-white' : 'bg-gray-100 text-gray-500'}`}>
                            <SafeIcon name={isOpen ? "Minus" : "Plus"} className="w-4 h-4" />
                          </div>
                        </button>
                        
                        <AnimatePresence>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2, ease: "easeInOut" }}
                              className="overflow-hidden"
                            >
                              <div className="px-6 pb-6 pt-2">
                                <p className="text-[#101F1F]/80 leading-relaxed mb-4 max-w-3xl">
                                  {feature.desc}
                                </p>
                                <div>
                                  <span className="text-xs font-bold text-[#101F1F]/50 uppercase tracking-widest block mb-1">
                                    Why it matters to your bottom line:
                                  </span>
                                  <p className="text-[#101F1F]/90 font-medium leading-relaxed max-w-3xl">
                                    {feature.bottomLine}
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="max-w-4xl mx-auto px-6 text-center mt-32">
          <h2 className="text-3xl md:text-4xl font-bold text-[#101F1F] mb-6">
            Want to see the engine instead of reading the list?
          </h2>
          <p className="text-lg text-[#101F1F]/70 mb-10 max-w-2xl mx-auto">
            Open the Playground and watch one review become a reply, a post, an insight, and a next step.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/playground" className="w-full sm:w-auto bg-[#F49558] hover:bg-[#D35E0E] text-white text-lg font-bold py-4 px-10 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2">
              Try the App Live <SafeIcon name="ArrowRight" className="w-5 h-5" />
            </Link>
            <Link to="/download" className="w-full sm:w-auto bg-white hover:bg-gray-50 text-[#101F1F] border border-gray-200 shadow-sm text-lg font-bold py-4 px-10 rounded-xl transition-all flex items-center justify-center">
              Download the App
            </Link>
          </div>
        </div>

      </main>
      
      <Footer />
    </div>
  );
}